import { debounce } from '@mui/material'
import { EquipmentType, YardBlockAreaPartialDto } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import yardBlockService from '@operations/services/yardBlockService'
import { TomAutoComplete } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useCallback, useState } from 'react'
import { YardBlockOption } from '../atoms/YardBlockOption'

interface Props {
  label: string
  selectedId?: number | null
  initialFilterName?: string
  equipmentType?: EquipmentType
  dataCy?: string
  onChange: (value: YardBlockAreaPartialDto | null | undefined) => void
  onLoadBays: (value: string[]) => void
}
export const YardBlockAreaAutoComplete = observer(
  ({
    label,
    equipmentType,
    selectedId,
    initialFilterName,
    dataCy,
    onChange,
    onLoadBays,
  }: Props) => {
    const [yardBlocks, setYardBlocks] = useState<YardBlockAreaPartialDto[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const { appStore } = useOperationsStore()

    const fetchYardBlocks = useCallback(
      async (filter?: string) => {
        const yardBlocks = await yardBlockService.getYardBlocksByPartialName(filter, [
          equipmentType ?? EquipmentType.Rs,
        ])
        setYardBlocks(yardBlocks)

        return yardBlocks
      },
      [equipmentType],
    )

    const { loading } = useAsyncFetch(async () => {
      if (initialFilterName) {
        await appStore.triggerRequestWithoutLoader(async () => {
          const initialYardBlocks = await fetchYardBlocks(initialFilterName)
          onLoadBays(initialYardBlocks.find(x => x.workAreaId === selectedId)?.bays ?? [])
        })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchYardBlocks])

    const filterChange = debounce(async (filter?: string) => {
      if (!filter) {
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      await appStore.triggerRequestWithoutLoader(async () => {
        await fetchYardBlocks(filter)
      })
      setIsLoading(false)
    }, 500)

    return (
      <TomAutoComplete
        enablePortal
        label={label}
        entityKey={'workAreaId'}
        entityDisplayValue={'name'}
        loading={isLoading || loading}
        options={yardBlocks}
        defaultValue={yardBlocks.find(x => x.workAreaId === selectedId)}
        onChangeEntity={entity => onChange(entity)}
        onChangeFilter={filter => filterChange(filter)}
        renderOption={(props, option) => (
          <YardBlockOption
            key={`yard-block-${option.id}`}
            name={option.name!}
            renderProps={props}
          />
        )}
        dataCy={dataCy ?? 'yard-block-partial-autocomplete'}
      />
    )
  },
)
